import { useCallback, useEffect, useState } from 'react';
import { formatTimecode } from '@kinop/shared';
import { ApiError, listOpenRooms, type OpenRoom } from '../lib/api.js';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '../components/UserAvatar.js';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';

interface OpenRoomsPageProps {
  onNavigate: (path: string) => void;
}

/** Список живой: комнаты появляются и исчезают, пока человек на него смотрит. */
const REFRESH_MS = 5000;

/**
 * Открытые комнаты.
 *
 * Сюда попадают только те, где ведущий оставил включённой «Открытую
 * комнату», и только пока в них кто-то есть: комната без единого человека —
 * строка в базе, а не сеанс, и звать туда некого.
 */
export function OpenRoomsPage({ onNavigate }: OpenRoomsPageProps): JSX.Element {
  const [search,setSearch]=useState('');
  const [filter,setFilter]=useState('all');
  const [rooms, setRooms] = useState<OpenRoom[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const result = await listOpenRooms();
      setRooms(result.rooms);
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof ApiError ? loadError.message : 'Не удалось получить список.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const stage = (room: OpenRoom) => room.positionMs < 15*60000 ? 'start' : room.durationMs && room.positionMs / room.durationMs > .75 ? 'late' : 'mid';
  const found=rooms.filter(r=>(r.name+' '+(r.title??'')).toLowerCase().includes(search.toLowerCase()));
  const shown=found.filter(r=>filter==='all'||stage(r)===filter);
  return (
    <main className="shell">
      <h1 className="catalog__title">Открытые комнаты</h1>
      <p className="catalog__lede">
        Заходите к кому угодно и смотрите вместе. Комната появляется здесь, пока в ней кто-то
        есть и ведущий не закрыл вход.
      </p>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {!isLoading && rooms.length === 0 && !error && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia>🍿</EmptyMedia>
            <EmptyTitle>Сейчас никто не смотрит</EmptyTitle>
            <EmptyDescription>
              Откройте свою комнату — она появится в этом списке, и к вам смогут
              присоединиться. Закрыть вход можно в любой момент, на вкладке «Люди».
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => onNavigate('/new')}>Создать комнату</Button>
          </EmptyContent>
        </Empty>
      )}

      <div className="filter-bar"><input className="input" placeholder="Найти комнату или фильм" aria-label="Поиск комнат" value={search} onChange={e=>setSearch(e.target.value)}/><div className="choice-row">{[['all','Все'],['start','Только начали'],['mid','В середине'],['late','К финалу']].map(([k,label])=><button key={k} aria-pressed={filter===k} onClick={()=>setFilter(k!)}>{label} · {found.filter(r=>k==='all'||stage(r)===k).length}</button>)}</div></div>
      {!isLoading&&rooms.length>0&&shown.length===0&&<p className="helper">Ничего не нашлось. Попробуйте другой запрос или фильтр.</p>}
      <div className="room-list">
        {shown.map((room) => (
          <article key={room.code} className="room-card">
            <div className="room-stage" style={{color:stage(room)==='late'?'var(--danger)':stage(room)==='mid'?'var(--warn)':'var(--ok)'}}>{formatTimecode(room.positionMs)}<p className="helper">от начала</p></div><div className="room-card__head">
              <h2 className="room-card__name">{room.name}</h2>
              <Badge variant={room.isPlaying ? 'success' : 'secondary'}>
                {room.isPlaying ? 'идёт' : 'на паузе'}
              </Badge>
            </div>

            <p className="room-card__now">
              {room.title ? (
                <>
                  <span className="room-card__title">{room.title}</span>
                  <span className="room-card__at mono">{formatTimecode(room.positionMs)}</span>
                </>
              ) : (
                <span className="room-card__idle">видео ещё не выбрано</span>
              )}
            </p>

            <div className="room-card__people">
              <div className="room-card__faces">
                {room.participants.slice(0, 5).map((person, index) => (
                  <UserAvatar
                    key={`${person.nickname}-${index}`}
                    nickname={person.nickname}
                    avatar={person.avatar}
                    className="avatar--sm room-card__face"
                  />
                ))}
              </div>
              <span className="room-card__count mono">
                {room.participantCount}{' '}
                {room.participantCount === 1
                  ? 'человек'
                  : room.participantCount < 5
                    ? 'человека'
                    : 'человек'}
              </span>
            </div>

            <Button className="w-full" onClick={() => onNavigate(`/r/${room.code}`)}>
              {stage(room)==='late'?'Всё равно войти':'Присоединиться'}
            </Button>
          </article>
        ))}
      </div>
    </main>
  );
}
